// SEO-oriented long-form blog posts served at /blog and /blog/{slug}.
// Model mirrors philosopherPages.js: adding a new post is a matter of
// appending one object to BLOG_POSTS. Nothing in this file feeds back into
// the scoring engine.
//
// Post shape:
//   { slug, title, h1?, metaDescription, datePublished, author?,
//     coverImage?: { src, alt, credit? },   // used under H1 + og:image + JSON-LD
//     intro: string[],
//     sections: [ { h2? | h3?, paragraphs, list?, afterList?,
//                   image?: { src, alt, caption? }, closingLinks? } ] }
//
// Rules for section images: alt is mandatory (SEO). If the image field is
// missing or its `src` is empty, no <figure> is rendered.
// A section is either an <h2> block or an <h3> block. Blockquotes are pulled
// automatically from paragraphs that start with a straight quote character.

export const BLOG_POSTS = [
  {
    slug: "marcus-aurelius-personality-type",
    title: "Marcus Aurelius Personality Type: What the Meditations Reveal",
    h1: "Marcus Aurelius Personality Type: What the Meditations Actually Reveal",
    metaDescription:
      "Most answers say INFJ and stop there. Marcus Aurelius left a private journal — here is what his own words reveal about his personality, trait by trait.",
    datePublished: "2026-08-10",
    author: "Jonathan Mendola",
    // Cover image reuses the same Wikimedia bust already used on
    // /philosophers/marcus-aurelius — same file, same CC BY-SA 4.0 credit.
    coverImage: {
      src: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/ec/MSR-ra-61-b-1-DM.jpg/1024px-MSR-ra-61-b-1-DM.jpg",
      alt: "Marble bust of Marcus Aurelius, Roman emperor and Stoic philosopher",
      credit: {
        author: "Daniel Martin",
        license: "CC BY-SA 4.0",
        licenseUrl: "https://creativecommons.org/licenses/by-sa/4.0/",
        source: "https://commons.wikimedia.org/wiki/File:MSR-ra-61-b-1-DM.jpg",
      },
    },
    intro: [
      "Ask the internet what Marcus Aurelius's personality type was and you get a letter code. INFJ, usually. Sometimes INTJ. A number for the Enneagram, a four-letter label, and that's the end of it.",
      "It's a strange way to answer the question, because Marcus Aurelius is one of the very few historical figures who left us something better than a biography: a private journal he never intended anyone to read. The Meditations were notes to himself, written at night in military camps, with no audience to impress. If you want to know what someone was actually like, that's the document you want.",
      "So here is the short answer, and then the longer one that actually holds up.",
    ],
    sections: [
      {
        h2: "The short answer: INFJ, and why that's not enough",
        paragraphs: [
          "The consensus across typology databases puts Marcus Aurelius at INFJ — introverted, intuitive, feeling, judging — often paired with Enneagram type 1, the Reformer. The reasoning is reasonable enough: he was reflective rather than sociable, driven by principle rather than pleasure, and preoccupied with duty to the point of self-reproach.",
          "But there's a problem with typing a man who died in 180 AD. MBTI was designed as a self-report instrument. Nobody administered it to Marcus Aurelius. Every letter assigned to him is an inference drawn by a stranger from a text written eighteen centuries ago — and most of those inferences rest on the same handful of famous quotes, recycled across sites that never cite a passage.",
          "There's a better method available, and it costs nothing: read what he actually wrote about himself.",
        ],
      },
      {
        h2: "What his own words reveal, trait by trait",
        paragraphs: [],
      },
      {
        h3: "Discipline: the dominant trait, and he had to fight for it",
        paragraphs: [
          "The most repeated theme in the Meditations is not wisdom or virtue. It's getting out of bed.",
          "He argues with himself about it — at length, more than once. A man who had absolute power over the Roman Empire spent his private pages talking himself into starting the day. That detail matters more than any letter code: it tells you his discipline was not natural. It was constructed, daily, against resistance.",
          "\"Waste no more time arguing what a good man should be. Be one.\"",
          "That's not the sentence of someone to whom virtue comes easily. It's the sentence of someone who catches himself theorising instead of acting, and says so.",
        ],
      },
      {
        h3: "Serenity: the goal, not the starting point",
        paragraphs: [
          "Marcus Aurelius is remembered as the calm emperor. The journal shows the machinery underneath that calm — and it's effortful.",
          "\"You have power over your mind — not outside events. Realize this, and you will find strength.\"",
          "He repeats variations of this idea dozens of times. People do not repeat what they have already mastered. He was reminding himself, over and over, because the reminder kept being necessary: plague, war on the Danube, the death of most of his children, a rebellion led by a trusted general. His serenity was a practice, not a temperament.",
        ],
      },
      {
        h3: "Empathy: unusually high, for an emperor",
        paragraphs: [
          "One of the most distinctive things about the Meditations is how he prepares for other people's failures.",
          "He tells himself each morning that he will meet the ungrateful, the arrogant and the dishonest — and that they act that way from ignorance of what is good, not from malice. It's a remarkable posture for someone with the legal power to have any of them killed. He chooses to understand them instead.",
          "\"The best revenge is not to be like your enemy.\"",
        ],
      },
      {
        h3: "Resistance and ambition: much lower than you'd expect",
        paragraphs: [
          "Here's where the standard typology misses something. Marcus Aurelius held the most powerful office in the world and shows almost no appetite for it. He didn't seek the throne; he inherited it through adoption, and the journal reads like a man performing a duty he never asked for.",
          "He's equally uninterested in defiance for its own sake. Where Diogenes mocked convention and Nietzsche attacked it, Marcus worked inside the system he was handed and tried to be less bad at it each day. Low ambition, low resistance, extremely high discipline — that combination is rare, and it's the real signature of his personality.",
        ],
      },
      {
        h2: "The six-trait profile",
        paragraphs: [
          "Letter codes flatten people into boxes. A more useful approach is to score the traits independently and look at the shape they make together.",
          "On the six-trait model we use — discipline, curiosity, resistance, empathy, ambition, serenity — Marcus Aurelius comes out roughly like this:",
        ],
        chart: {
          title: "Marcus Aurelius — six-trait profile",
          testid: "chart-marcus",
          scores: [
            { label: "Serenity", value: 95, note: "The organising goal of his entire practice" },
            { label: "Discipline", value: 92, note: "Not innate, deliberately built" },
            { label: "Resistance", value: 82, note: "He refuses corruption, not convention" },
            { label: "Empathy", value: 68, note: "Systematic, almost procedural compassion" },
            { label: "Curiosity", value: 60, note: "Reflective, but not chasing new ideas" },
            { label: "Ambition", value: 50, note: "The most powerful man alive, writing about how little power matters" },
          ],
        },
        list: [
          "Serenity: very high. The organising goal of his entire practice.",
          "Discipline: very high. Not innate, deliberately built.",
          "Resistance: high but quiet. He refuses corruption, not convention.",
          "Empathy: above average. Systematic, almost procedural compassion.",
          "Curiosity: moderate. He reads and reflects, but he isn't chasing new ideas the way Socrates was.",
          "Ambition: low. The most powerful man alive, writing about how little power matters.",
        ],
        afterList: [
          "That profile is what makes him distinct. Plenty of people score high on discipline. What's rare is high discipline paired with low ambition — the willingness to work relentlessly at something you don't particularly want for yourself.",
        ],
        crossLink: {
          before: "For the exact mirror image of this profile — near-zero serenity paired with maximum ambition — see the ",
          linkText: "Nietzsche personality type",
          to: "/blog/nietzsche-personality-type",
          after: ".",
        },
      },
      {
        h2: "Would your chart look like his?",
        paragraphs: [
          "The honest answer to \"what personality type was Marcus Aurelius\" is that no four letters can carry it. What you can do is compare the shape of his traits to the shape of yours.",
        ],
        // The closing paragraph carries the three internal links. Rendered as
        // a dedicated JSX block by BlogPost so the anchors are semantic.
        closingLinks: true,
      },
    ],
  },
  {
    slug: "nietzsche-personality-type",
    title: "Nietzsche Personality Type: The INFJ vs INTJ Debate, Settled Differently",
    h1: "Nietzsche Personality Type: The INFJ vs INTJ Debate, Settled Differently",
    metaDescription:
      "Typology forums have argued for years over whether Nietzsche was INFJ or INTJ. He wrote something that makes the whole question awkward — here is what his texts actually show.",
    datePublished: "2026-08-10",
    author: "Jonathan Mendola",
    // Cover image reuses the same Wikimedia portrait already used on
    // /philosophers/nietzsche (public domain).
    coverImage: {
      src: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1b/Nietzsche187a.jpg/1024px-Nietzsche187a.jpg",
      alt: "Portrait photograph of Friedrich Nietzsche, 1875",
      credit: {
        author: "Friedrich Hermann Hartmann",
        license: "Public domain",
        source: "https://commons.wikimedia.org/wiki/File:Nietzsche187a.jpg",
      },
    },
    intro: [
      "There is a small, persistent argument on the internet about Friedrich Nietzsche. One camp says INFJ. The other says INTJ. Forum threads run for pages. Both sides make decent points, and neither can win, because the evidence they're arguing over is a set of quotes stripped from their books.",
      "There's something funnier underneath it, though. Nietzsche wrote a line that makes the entire exercise uncomfortable:",
      "\"I mistrust all systematizers and avoid them. The will to a system is a lack of integrity.\"",
      "The man being sorted into a four-letter box spent his career attacking the impulse to sort people into boxes. That doesn't mean we can't say anything about his personality. It means we should describe him instead of labelling him.",
    ],
    sections: [
      {
        h2: "Why both camps are half right",
        paragraphs: [
          "The INTJ case rests on the architecture of his thinking: he built a systematic critique of morality, worked alone, and was ruthlessly analytical about ideas everyone else treated as sacred.",
          "The INFJ case rests on his intensity: the prose is closer to poetry than philosophy, he was preoccupied with meaning and suffering rather than with mechanisms, and Thus Spoke Zarathustra reads like a prophecy, not a treatise.",
          "Both readings are describing something real. That's exactly the problem with the method — a system that forces a choice between \"thinking\" and \"feeling\" has nothing useful to say about a man whose entire project was that the two are not separable.",
        ],
      },
      {
        h2: "What his texts actually show",
        paragraphs: [],
      },
      {
        h3: "Ambition: the highest score in the set",
        paragraphs: [
          "Not ambition for money or office. Ambition for consequence.",
          "Nietzsche believed his books would split the history of thought in two, and he said so, in writing, before anyone had read them. He titled a chapter of his autobiography \"Why I Write Such Good Books.\" He described himself as dynamite. Whatever you make of that, it is not the self-description of a modest man.",
          "This is the trait that separates him most sharply from Marcus Aurelius, who held actual imperial power and wrote about how little it mattered. Nietzsche had no power at all and wanted to remake civilisation.",
        ],
      },
      {
        h3: "Resistance: near maximum",
        paragraphs: [
          "His work is one long refusal. Refusal of Christian morality, of German nationalism, of academic philosophy, of Wagner after years of friendship, of pity as a virtue, of the herd in all its forms.",
          "\"Become who you are.\"",
          "Three words that are usually read as gentle encouragement. In context they are the opposite of gentle: they mean stop being what your upbringing, your religion and your neighbours made you. It is an instruction to break something.",
        ],
      },
      {
        h3: "Serenity: the lowest score in the set",
        paragraphs: [
          "This is the trait most people get wrong about him, because the writing sounds so forceful.",
          "Nietzsche was chronically ill for most of his adult life — migraines that cost him days at a time, near-blindness, stomach collapse. He resigned his professorship at 34 and spent the following decade moving between boarding houses looking for air that hurt less. He wrote almost everything he is famous for in that state.",
          "\"What does not kill me makes me stronger\" is not a gym slogan when you know who wrote it and under what conditions. It is a man arguing with his own body.",
        ],
      },
      {
        h3: "Empathy: low, and deliberately so",
        paragraphs: [
          "He attacked pity directly and repeatedly, arguing that it degrades both the person who gives it and the person who receives it. He kept few friendships and ended most of them himself.",
          "There's a case that this was philosophical rather than temperamental — that he valued a harder kind of respect over comfort. It doesn't change the score. On the axis of how much other people's states weigh on your decisions, he sits low.",
        ],
      },
      {
        h2: "The six-trait profile",
        paragraphs: [
          "On the same six-trait model, Nietzsche's chart is almost the opposite shape of Marcus Aurelius's.",
        ],
        chart: {
          title: "Friedrich Nietzsche — six-trait profile",
          testid: "chart-nietzsche",
          scores: [
            { label: "Ambition", value: 97, note: "He wanted to change what humans consider good" },
            { label: "Resistance", value: 90, note: "Almost every book is a refusal of something" },
            { label: "Curiosity", value: 82, note: "Philology, music, history, biology — he read across everything" },
            { label: "Discipline", value: 55, note: "Enormous output, but in erratic bursts between collapses" },
            { label: "Empathy", value: 25, note: "Pity was a target, not a value" },
            { label: "Serenity", value: 18, note: "Nothing about his inner life was calm" },
          ],
        },
        // Rendered as a paragraph with an inline Link to the Marcus article.
        crossLink: {
          before: "Set that next to ",
          linkText: "Marcus Aurelius",
          to: "/blog/marcus-aurelius-personality-type",
          after: " and you get the two poles of the model: very high serenity with low ambition on one side, very low serenity with maximum ambition on the other. Two ways of being serious about how to live, pointing in opposite directions.",
        },
      },
      {
        h2: "Which one is closer to you?",
        paragraphs: [
          "You almost certainly don't score like either of them. Most people don't sit at the extremes — that's what makes the extremes memorable.",
        ],
        closingLinks: true,
      },
    ],
  },
];

export const BLOG_SLUG_TO_POST = BLOG_POSTS.reduce((acc, p) => {
  acc[p.slug] = p;
  return acc;
}, {});
